import React, { useEffect, useRef } from 'react';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import {
  createPlainTextPasteHandler,
  STIMULUS_IMAGE_PASTE_MESSAGE,
} from '../../utils/clipboardPaste';

const TOOLBAR_OPTIONS = [
  [{ font: [] }, { size: [] }],
  ['bold', 'italic', 'underline', 'strike'],
  [{ align: [] }],
  [{ list: 'ordered' }, { list: 'bullet' }],
  ['clean'],
];

const StimulusRichTextEditor = ({ value, onChange, placeholder }) => {
  const editorRef = useRef(null);
  const quillRef = useRef(null);
  const lastHtmlRef = useRef('');
  const lastSelectionRef = useRef(null);
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (!editorRef.current || quillRef.current) {
      return;
    }
    const quill = new Quill(editorRef.current, {
      modules: { toolbar: TOOLBAR_OPTIONS },
      theme: 'snow',
      placeholder: placeholder || 'Start here...',
    });
    const Delta = Quill.import('delta');
    quill.root.style.fontFamily = 'var(--font-family-ibm_plex_sans)';
    quill.root.style.fontSize = '16px';
    quill.root.style.lineHeight = '24px';
    quill.root.style.color = '#181718';

    const delta = quill.clipboard.convert({ html: value || '' });
    quill.setContents(delta, 'silent');
    lastHtmlRef.current = quill.root.innerHTML;
    quillRef.current = quill;

    const handleSelectionChange = (range) => {
      if (range) {
        lastSelectionRef.current = range;
      }
    };

    const handleTextChange = (_delta, _oldDelta, source) => {
      if (source !== 'user') {
        return;
      }
      const html = quill.root.innerHTML;
      lastHtmlRef.current = html;
      lastSelectionRef.current = quill.getSelection() || lastSelectionRef.current;
      onChangeRef.current?.(html);
    };

    quill.on('selection-change', handleSelectionChange);
    quill.on('text-change', handleTextChange);

    const handlePaste = createPlainTextPasteHandler(quill, {
      imagePasteMessage: STIMULUS_IMAGE_PASTE_MESSAGE,
    });

    quill.clipboard.addMatcher('img', () => new Delta());
    quill.root.addEventListener('paste', handlePaste, true);

    const toolbar = quill.getModule('toolbar');
    const toolbarContainer = toolbar?.container || null;
    const handleToolbarMouseDown = () => {
      if (!quill.hasFocus()) {
        quill.focus();
        const range = lastSelectionRef.current;
        if (range) {
          quill.setSelection(range.index, range.length, 'silent');
        } else {
          const index = Math.max(0, quill.getLength() - 1);
          quill.setSelection(index, 0, 'silent');
        }
      }
    };

    if (toolbarContainer) {
      toolbarContainer.addEventListener('mousedown', handleToolbarMouseDown);
      toolbarContainer.addEventListener('touchstart', handleToolbarMouseDown, {
        passive: true,
      });
    }

    return () => {
      quill.off('selection-change', handleSelectionChange);
      quill.off('text-change', handleTextChange);
      quill.root.removeEventListener('paste', handlePaste, true);
      if (toolbarContainer) {
        toolbarContainer.removeEventListener('mousedown', handleToolbarMouseDown);
        toolbarContainer.removeEventListener('touchstart', handleToolbarMouseDown);
      }
    };
  }, []);

  useEffect(() => {
    const quill = quillRef.current;
    if (!quill) {
      return;
    }
    if (quill.hasFocus()) {
      return;
    }
    const nextValue = value || '';
    if (nextValue === lastHtmlRef.current) {
      return;
    }
    if (nextValue === quill.root.innerHTML) {
      lastHtmlRef.current = nextValue;
      return;
    }
    const selection = quill.getSelection() || lastSelectionRef.current;
    const delta = quill.clipboard.convert({ html: nextValue });
    quill.setContents(delta, 'silent');
    lastHtmlRef.current = quill.root.innerHTML;
    if (selection) {
      const maxIndex = Math.max(0, quill.getLength() - 1);
      const index = Math.min(selection.index, maxIndex);
      quill.setSelection(index, selection.length);
    }
  }, [value]);

  return <div ref={editorRef} />;
};

export default StimulusRichTextEditor;
